import axios, { type AxiosInstance, type AxiosRequestConfig } from 'axios';
import { handleUnauthorized } from './unauthorized';

export type SurveyStatus = 'draft' | 'published' | 'closed';
export interface SurveyOption { id: string; label: string; exclusive: boolean }
export interface SurveyQuestion { id: string; title: string; type: 'single' | 'multiple' | 'text'; required: boolean; options: SurveyOption[] }
export interface SurveyDraft { title: string; description: string; questions: SurveyQuestion[] }
export interface SurveyDefinition extends SurveyDraft { slug: string }
export interface SurveyDetail { definition: SurveyDefinition; status: SurveyStatus; revision: number; createdAt: string; publishedAt: string | null; closedAt: string | null }
export interface ManagedSurvey { survey: SurveyDetail; publicUrl: string | null }
export interface SurveySummary { slug: string; title: string; status: SurveyStatus; revision: number; total: number; createdAt: string }
export interface SurveyListing { items: SurveySummary[]; total: number; page: number; size: number }
export interface SurveyOverview {
  publicUrl: string | null;
  statistics: {
    definition: SurveyDefinition;
    status: SurveyStatus;
    total: number;
    counts: { questionId: string; optionId: string; count: number }[];
    answeredCounts: { questionId: string; count: number }[];
    feedbackQuestionId: string | null;
    feedback: { text: string; submittedAt: string }[];
    feedbackTotal: number;
    page: number;
  };
}
const surveyPath = (slug: string) => `/surveys/${encodeURIComponent(slug)}`;
export async function listSurveys(page = 1): Promise<SurveyListing> { return (await http.get('/surveys', { params: { page, size: 20 } })).data; }
export async function getSurvey(slug: string): Promise<ManagedSurvey> { return (await http.get(surveyPath(slug))).data; }
export async function createSurvey(draft: SurveyDraft): Promise<ManagedSurvey> { return (await http.post('/surveys', draft)).data; }
export async function updateSurvey(slug: string, revision: number, draft: SurveyDraft): Promise<ManagedSurvey> { return (await http.put(surveyPath(slug), { revision, draft })).data; }
export async function changeSurveyStatus(slug: string, action: 'publish' | 'close', revision: number): Promise<ManagedSurvey> { return (await http.post(`${surveyPath(slug)}/${action}`, { revision })).data; }
export async function copySurvey(slug: string): Promise<ManagedSurvey> { return (await http.post(`${surveyPath(slug)}/copy`)).data; }
export async function getSurveyStatistics(slug: string, page = 1, questionId?: string): Promise<SurveyOverview> {
  return (await http.get(`${surveyPath(slug)}/statistics`, { params: { page, questionId } })).data;
}

const baseURL = import.meta.env.VITE_ADMIN_BASE
  ? `${import.meta.env.VITE_ADMIN_BASE}/admin/api`
  : '/admin/api';

/**
 * 坐席实时 WS 地址（/admin/ws/support）。token 走 query（WS 握手带不上 Authorization 头）。
 * VITE_ADMIN_BASE 设置时直连该 host，否则走同源（dev 由 Vite ws 代理转发，prod 经 Caddy）。
 */
export function supportAgentWsUrl(token: string): string {
  const base = import.meta.env.VITE_ADMIN_BASE || window.location.origin;
  const wsBase = base.replace(/^http/i, 'ws'); // http→ws / https→wss
  return `${wsBase}/admin/ws/support?token=${encodeURIComponent(token)}`;
}

const http: AxiosInstance = axios.create({
  baseURL,
  timeout: 15_000,
  withCredentials: false
});

http.interceptors.request.use((config) => {
  const token = localStorage.getItem('lulupaw_admin_token');
  if (token) {
    config.headers = config.headers ?? {};
    (config.headers as Record<string, string>).Authorization = `Bearer ${token}`;
  }
  return config;
});

http.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      handleUnauthorized();
    }
    return Promise.reject(error);
  }
);

export async function ping(): Promise<{
  ok: boolean;
  service: string;
  port: number;
  timestamp: string;
}> {
  const res = await http.get('/ping');
  return res.data;
}

// ============================================================
// System / server monitoring (dashboard)
// ============================================================

export interface ServerInfo {
  collected_at: string;
  os: { name: string; version: string; arch: string };
  host: { hostname: string; ip: string };
  cpu: { cores: number; usage_percent: number | null };
  memory: {
    total_bytes: number;
    used_bytes: number;
    free_bytes: number;
    usage_percent: number | null;
  };
  disks: Array<{
    mount_path: string;
    total_bytes: number;
    used_bytes: number;
    free_bytes: number;
    usage_percent: number | null;
  }>;
  jvm: {
    version: string;
    vendor: string;
    heap_used_bytes: number;
    heap_max_bytes: number;
    heap_usage_percent: number | null;
  };
  runtime: {
    application: string;
    uptime_seconds: number;
    jvm_name: string;
    jvm_version: string;
  };
}

export function fetchServerInfo(): Promise<ServerInfo> {
  return get<ServerInfo>('/system/server-info');
}

/** C 端（app-bootstrap，8080）健康/负载快照（B 端后台定时探测的缓存）。 */
export interface AppHealth {
  collected_at: string | null;
  base_url: string;
  enabled: boolean;
  reachable: boolean;
  status: 'UP' | 'DOWN' | 'UNKNOWN' | 'DISABLED';
  latency_ms: number | null;
  error: string | null;
  cpu: {
    cores: number | null;
    system_usage_percent: number | null;
    process_usage_percent: number | null;
    load_average_1m: number | null;
  } | null;
  memory: {
    heap_used_bytes: number | null;
    heap_max_bytes: number | null;
    heap_usage_percent: number | null;
    nonheap_used_bytes: number | null;
  } | null;
  threads: { live: number | null; daemon: number | null; peak: number | null } | null;
  db: {
    active: number | null;
    idle: number | null;
    max: number | null;
    pending: number | null;
  } | null;
  disk: {
    free_bytes: number | null;
    total_bytes: number | null;
    usage_percent: number | null;
  } | null;
  uptime_seconds: number | null;
}

export function fetchAppHealth(): Promise<AppHealth> {
  return get<AppHealth>('/system/app-health');
}

export function get<T = unknown>(url: string, config?: AxiosRequestConfig): Promise<T> {
  return http.get<T>(url, config).then((res) => res.data);
}

export function post<T = unknown>(url: string, body?: unknown, config?: AxiosRequestConfig): Promise<T> {
  return http.post<T>(url, body, config).then((res) => res.data);
}

/** 下载需要管理后台 Bearer 鉴权的二进制内容。调用方负责管理 Object URL 生命周期。 */
export function getBlob(url: string, config?: AxiosRequestConfig): Promise<Blob> {
  return http.get<Blob>(url, { ...config, responseType: 'blob' }).then((res) => res.data);
}

// ============================================================
// Catalog: Device Types + Product Models
// ============================================================

/**
 * 设备统计卡片维度。
 *
 * - `toilet` / `food` / `water` 同时服务 C 端首页 Today 与宠物 Tab。
 * - `body_weight` 仅服务宠物 Tab（C 端首页 scope 过滤）。
 */
export type DeviceStatCategory = 'toilet' | 'food' | 'water' | 'body_weight';

export const DEVICE_STAT_CATEGORIES: { value: DeviceStatCategory; label: string; scope: '通用' | '仅宠物' }[] = [
  { value: 'toilet', label: '如厕', scope: '通用' },
  { value: 'food', label: '进食', scope: '通用' },
  { value: 'water', label: '饮水', scope: '通用' },
  { value: 'body_weight', label: '体重', scope: '仅宠物' },
];

export interface DeviceType {
  id: string;
  code: string;
  name: string;
  /** 兼容字段：等于 statCategories[0]；新代码请用 statCategories。 */
  statCategory: DeviceStatCategory;
  statCategories: DeviceStatCategory[];
  icon?: string;
  description?: string;
  isActive: boolean;
  sort: number;
  modelCount: number;
  deviceCount: number;
  createdAt: string;
  updatedAt: string;
}

/** 型号对比行结构；商城商品使用 mall.products.compare_specs_json。 */
export interface CompareSpec {
  /** 历史图标字段；官网新版对比表不显示。 */
  icon: string;
  /** 大字值，如「2.0 L」。 */
  value: string;
  /** 两列之间的共同标题，如「水箱容量」。 */
  label: string;
  /** 颜色行展示色块与名称；旧数据缺省为普通文字行。 */
  type?: 'text' | 'color';
  colors?: { name: string; hex: string }[];
}

export interface ProductModel {
  id: string;
  code: string;
  deviceTypeId: string;
  name: string;
  vendor?: string;
  hardwareVersion?: string;
  firmwareMin?: string;
  firmwareLatest?: string;
  capabilities: string[];
  recognitionMaterialRequirements: string[];
  hasCamera: boolean;
  hasTalkback: boolean;
  imageUrl?: string;
  description?: string;
  uiKind: string;
  isActive: boolean;
  sort: number;
  deviceCount: number;
  /** 已启用的详情页 block 数量（admin.product_model_detail_blocks）。 */
  detailBlockCount: number;
  /** 已启用的设置页 shell 段数量（admin.product_model_shell_sections）。 */
  shellSectionCount: number;
  /** 已启用的设备设置字段总数（admin.product_model_settings_fields）。 */
  settingsFieldCount: number;
  /** 已启用的事件通知候选数量（admin.product_model_event_notifications）。 */
  eventNotificationCount: number;
  /** 已启用的告警规则数量（admin.product_model_alert_rules）。 */
  alertRuleCount: number;
  /** 该型号设备每「首次绑定」为家庭授予的数字分身生成次数（0=不授予）。 */
  mascotGenerationQuota: number;
  /** 该型号设备保修期天数（默认 365）；设备首次配网时快照锁定。 */
  warrantyDays: number;
  createdAt: string;
  updatedAt: string;
}

export interface PageResponse<T> {
  items: T[];
  total: number;
  page: number;
  size: number;
}

export interface DeviceTypeListParams {
  active?: boolean;
  q?: string;
  page?: number;
  size?: number;
}

export interface CreateDeviceTypeBody {
  code: string;
  name: string;
  /** 至少一项；后端会校验子集合法性与去重。 */
  statCategories: DeviceStatCategory[];
  icon?: string;
  description?: string;
  sort?: number;
}

export type UpdateDeviceTypeBody = Partial<{
  name: string;
  /** 提交则覆盖；不提交（undefined）保持不变。 */
  statCategories: DeviceStatCategory[];
  icon: string;
  description: string;
  sort: number;
  isActive: boolean;
}>;

export const deviceTypeApi = {
  list(params: DeviceTypeListParams = {}): Promise<PageResponse<DeviceType>> {
    return http
      .get<PageResponse<DeviceType>>('/device-types', { params })
      .then((res) => res.data);
  },
  get(id: string): Promise<DeviceType> {
    return http.get<DeviceType>(`/device-types/${id}`).then((res) => res.data);
  },
  create(body: CreateDeviceTypeBody): Promise<DeviceType> {
    return http.post<DeviceType>('/device-types', body).then((res) => res.data);
  },
  update(id: string, body: UpdateDeviceTypeBody): Promise<DeviceType> {
    return http.patch<DeviceType>(`/device-types/${id}`, body).then((res) => res.data);
  },
  toggle(id: string): Promise<DeviceType> {
    return http.post<DeviceType>(`/device-types/${id}/toggle`).then((res) => res.data);
  },
  remove(id: string): Promise<void> {
    return http.delete<void>(`/device-types/${id}`).then(() => undefined);
  }
};

export interface ProductModelListParams {
  deviceTypeId?: string;
  active?: boolean;
  q?: string;
  page?: number;
  size?: number;
}

export interface CreateProductModelBody {
  code: string;
  deviceTypeId: string;
  name: string;
  vendor?: string;
  hardwareVersion?: string;
  firmwareMin?: string;
  firmwareLatest?: string;
  capabilities?: string[];
  recognitionMaterialRequirements?: string[];
  hasCamera?: boolean;
  hasTalkback?: boolean;
  imageUrl?: string;
  description?: string;
  uiKind?: string;
  sort?: number;
  mascotGenerationQuota?: number;
  warrantyDays?: number;
}

export type UpdateProductModelBody = Partial<{
  name: string;
  vendor: string;
  hardwareVersion: string;
  firmwareMin: string;
  firmwareLatest: string;
  capabilities: string[];
  recognitionMaterialRequirements: string[];
  hasCamera: boolean;
  hasTalkback: boolean;
  imageUrl: string;
  description: string;
  uiKind: string;
  sort: number;
  mascotGenerationQuota: number;
  warrantyDays: number;
  isActive: boolean;
}>;

/** 产品型号配图预览 URL（本地上传存 C 端路径，Admin 走同源代理）。 */
export function productModelImagePreviewUrl(imageUrl?: string | null): string | null {
  if (!imageUrl?.trim()) return null;
  const trimmed = imageUrl.trim();
  if (trimmed.startsWith('http://') || trimmed.startsWith('https://')) {
    return trimmed;
  }
  const match = trimmed.match(/product-model-images\/([^/?#]+)(\?[^#]*)?(#.*)?$/);
  if (match) {
    const adminBase = import.meta.env.VITE_ADMIN_BASE ?? '';
    // 保留上传返回的内容版本参数；丢掉 ?v= 会继续命中浏览器里的旧图片。
    return `${adminBase}/admin/api/product-model-images/${match[1]}${match[2] ?? ''}${match[3] ?? ''}`;
  }
  return trimmed;
}

/**
 * 社区帖子配图预览 URL：库里存的是 C 端公开路径 `/api/v1/community-post-images/{file}`，
 * Admin 改走同源 `/admin/api/community-post-images/{file}`（vite 代理到 8090，无 CORS；
 * 该路径已在 AdminSecurityConfig 放行，`<img>` 不带 token 也能取）。
 */
/**
 * 客服图片（坐席头像 / 群发配图）在**后台**里的预览地址。
 *
 * 上传接口返回的是 C 端公开路径 `/api/v1/support-images/xxx`，直接塞进 `<img src>`
 * 浏览器会去请求后台自己的域名——那里没有这个路径，于是传完「看不到任何变化」。
 * 后端另备了 `/admin/api/support-images/{filename}` 给后台预览用，这里做转换。
 *
 * 与 communityImagePreviewUrl 同一套路数：绝对地址原样返回，相对路径取文件名重拼。
 */
export function supportImagePreviewUrl(imageUrl?: string | null): string | null {
  if (!imageUrl?.trim()) return null;
  const trimmed = imageUrl.trim();
  if (trimmed.startsWith('http://') || trimmed.startsWith('https://')) {
    return trimmed;
  }
  const match = trimmed.match(/support-images\/([^/?#]+)/);
  if (match) {
    const adminBase = import.meta.env.VITE_ADMIN_BASE ?? '';
    return `${adminBase}/admin/api/support-images/${match[1]}`;
  }
  return trimmed;
}

export function communityImagePreviewUrl(imageUrl?: string | null): string | null {
  if (!imageUrl?.trim()) return null;
  const trimmed = imageUrl.trim();
  if (trimmed.startsWith('http://') || trimmed.startsWith('https://')) {
    return trimmed;
  }
  const match = trimmed.match(/community-post-images\/([^/?#]+)/);
  if (match) {
    const adminBase = import.meta.env.VITE_ADMIN_BASE ?? '';
    return `${adminBase}/admin/api/community-post-images/${match[1]}`;
  }
  return trimmed;
}

/**
 * 官方话题封面预览 URL：库里存 C 端公开路径 `/api/v1/community-topic-covers/{file}`，
 * Admin 改走同源 `/admin/api/community-topic-covers/{file}`（已在 AdminSecurityConfig 放行）。
 */
export function communityTopicCoverPreviewUrl(coverUrl?: string | null): string | null {
  if (!coverUrl?.trim()) return null;
  const trimmed = coverUrl.trim();
  if (trimmed.startsWith('http://') || trimmed.startsWith('https://')) {
    return trimmed;
  }
  const match = trimmed.match(/community-topic-covers\/([^/?#]+)/);
  if (match) {
    const adminBase = import.meta.env.VITE_ADMIN_BASE ?? '';
    return `${adminBase}/admin/api/community-topic-covers/${match[1]}`;
  }
  return trimmed;
}

export const productModelApi = {
  list(params: ProductModelListParams = {}): Promise<PageResponse<ProductModel>> {
    return http
      .get<PageResponse<ProductModel>>('/product-models', { params })
      .then((res) => res.data);
  },
  get(id: string): Promise<ProductModel> {
    return http.get<ProductModel>(`/product-models/${id}`).then((res) => res.data);
  },
  create(body: CreateProductModelBody): Promise<ProductModel> {
    return http.post<ProductModel>('/product-models', body).then((res) => res.data);
  },
  update(id: string, body: UpdateProductModelBody): Promise<ProductModel> {
    return http.patch<ProductModel>(`/product-models/${id}`, body).then((res) => res.data);
  },
  toggle(id: string): Promise<ProductModel> {
    return http.post<ProductModel>(`/product-models/${id}/toggle`).then((res) => res.data);
  },
  remove(id: string): Promise<void> {
    return http.delete<void>(`/product-models/${id}`).then(() => undefined);
  },
  uploadImage(id: string, file: File): Promise<ProductModel> {
    const form = new FormData();
    form.append('file', file);
    return http
      .post<ProductModel>(`/product-models/${id}/image`, form, {
        headers: { 'Content-Type': 'multipart/form-data' }
      })
      .then((res) => res.data);
  }
};

/** 型号文档物料（承载包装二维码的稳定身份，见 V432）。 */
export interface ProductModelDoc {
  id: string;
  label: string;
  /** 内容地址（App 内置查看用）。 */
  url: string;
  /** 单文件落地路径；包装二维码使用型号页 /pack/model/{modelId}，域名随环境选择。 */
  landingPath: string;
  mimeType: string;
  sizeBytes: number;
  originalFilename: string | null;
  mediaResourceId: string | null;
  enabled: boolean;
  packVisible: boolean;
  sort: number;
}

export interface ProductModelDocUpload {
  /** 落盘后的存储文件名（随机 UUID），用于下一步创建/替换物料。 */
  storageKey: string;
}

/** 第一步：仅上传文件落盘。 */
export function uploadProductModelDoc(file: File): Promise<ProductModelDocUpload> {
  const form = new FormData();
  form.append('file', file);
  return http
    .post<ProductModelDocUpload>('/product-model-docs', form, {
      headers: { 'Content-Type': 'multipart/form-data' }
    })
    .then((res) => res.data);
}

/**
 * 第二步：把文件挂到型号上。
 *
 * **按 (型号, label) 复用**：同名已存在时后端只换文件指针，id 与地址不变 ——
 * 运营把"安装视频"重传一遍，拿到的 landingPath 与已印在包装上的完全一致。
 */
export function createProductModelDoc(
  modelId: string,
  body: {
    label: string;
    storageKey?: string;
    mediaResourceId?: string;
    mimeType?: string;
    sizeBytes?: number;
    originalFilename?: string;
    sort?: number;
  }
): Promise<{ url: string; docId: string; storageKey: string; landingPath: string }> {
  return http
    .post(`/product-models/${modelId}/docs`, body)
    .then((res) => res.data);
}

/** 替换已有物料的文件；docId 与包装二维码地址保持不变。 */
export function replaceProductModelDocFile(
  modelId: string,
  docId: string,
  body: {
    storageKey?: string;
    mediaResourceId?: string;
    mimeType?: string;
    sizeBytes?: number;
    originalFilename?: string;
  }
): Promise<{ url: string; docId: string; storageKey: string; landingPath: string }> {
  return http
    .put(`/product-models/${modelId}/docs/${docId}/file`, body)
    .then((res) => res.data);
}

export function listProductModelDocs(modelId: string): Promise<ProductModelDoc[]> {
  return http.get(`/product-models/${modelId}/docs`).then((res) => res.data);
}

export function importLegacyProductModelDoc(modelId: string, docId: string): Promise<ProductModelDoc> {
  return http.post<ProductModelDoc>(`/product-models/${modelId}/docs/${docId}/import-legacy`)
    .then((res) => res.data);
}

/** 配置包装二维码页入口的标题、显示状态和顺序；不改变 docId。 */
export function updateProductModelDoc(
  modelId: string,
  docId: string,
  body: { label?: string; packVisible?: boolean; sort?: number }
): Promise<void> {
  return http.patch(`/product-models/${modelId}/docs/${docId}`, body).then(() => undefined);
}

export function deleteProductModelDoc(modelId: string, docId: string): Promise<void> {
  return http.delete(`/product-models/${modelId}/docs/${docId}`).then(() => undefined);
}

// ============================================================
// Product Model Attributes
// ============================================================

export type AttributeValueType = 'bool' | 'int' | 'float' | 'string' | 'object';

export interface ProductModelAttribute {
  id: string;
  productModelId: string;
  field: string;
  fieldNameZh: string;
  fieldNameEn?: string | null;
  valueType: AttributeValueType;
  unit?: string;
  enabled: boolean;
  sort: number;
  createdAt: string;
  updatedAt: string;
}

export interface ProductModelAttributeListParams {
  enabled?: boolean;
  q?: string;
  page?: number;
  size?: number;
}

export interface CreateProductModelAttributeBody {
  field: string;
  fieldNameZh: string;
  fieldNameEn?: string;
  valueType: AttributeValueType;
  unit?: string;
  sort?: number;
}

export type UpdateProductModelAttributeBody = Partial<{
  fieldNameZh: string;
  fieldNameEn: string;
  valueType: AttributeValueType;
  unit: string;
  sort: number;
  enabled: boolean;
}>;

// ============================================================
// Device Ops (read-only)
// ============================================================

export interface OpsDeviceSummary {
  id: string;
  name: string;
  productId: string;
  type: string;
  serialNumber?: string;
  connectionState: string;
  familyId: string;
  reportedAt?: string;
  createdAt?: string;
}

export interface OpsDeviceSettingsShadowField {
  field: string;
  labelZh?: string;
  labelEn?: string;
  scope?: string;
  desired?: unknown;
  effectiveDesired?: unknown;
  reported?: unknown;
  pending: boolean;
  error?: unknown;
}

export interface OpsDeviceDetail extends OpsDeviceSummary {
  wifiSsid?: string;
  pairedAt?: string;
  settingsShadow: OpsDeviceSettingsShadowField[];
}

export interface OpsDeviceListParams {
  q?: string;
  productId?: string;
  connectionState?: string;
  familyId?: string;
  page?: number;
  size?: number;
}

export interface OpsDeviceHistoryPoint {
  ts: string;
  valueType: string;
  value?: unknown;
  displayValue?: string;
}

export interface OpsDeviceHistorySeries {
  field: string;
  fieldNameZh: string;
  fieldNameEn?: string;
  valueType: string;
  unit?: string;
  points: OpsDeviceHistoryPoint[];
}

export interface OpsDeviceHistoryParams {
  fields: string;
  from?: string;
  to?: string;
  limit?: number;
}

export const opsDeviceApi = {
  list(params: OpsDeviceListParams = {}): Promise<PageResponse<OpsDeviceSummary>> {
    return http.get<PageResponse<OpsDeviceSummary>>('/ops/devices', { params }).then((r) => r.data);
  },
  get(deviceId: string): Promise<OpsDeviceDetail> {
    return http.get<OpsDeviceDetail>(`/ops/devices/${deviceId}`).then((r) => r.data);
  },
  history(deviceId: string, params: OpsDeviceHistoryParams): Promise<OpsDeviceHistorySeries[]> {
    return http
      .get<OpsDeviceHistorySeries[]>(`/ops/devices/${deviceId}/history`, { params })
      .then((r) => r.data);
  }
};

export const productModelAttributeApi = {
  list(
    modelId: string,
    params: ProductModelAttributeListParams = {}
  ): Promise<PageResponse<ProductModelAttribute>> {
    return http
      .get<PageResponse<ProductModelAttribute>>(`/product-models/${modelId}/attributes`, {
        params
      })
      .then((res) => res.data);
  },
  get(modelId: string, attrId: string): Promise<ProductModelAttribute> {
    return http
      .get<ProductModelAttribute>(`/product-models/${modelId}/attributes/${attrId}`)
      .then((res) => res.data);
  },
  create(modelId: string, body: CreateProductModelAttributeBody): Promise<ProductModelAttribute> {
    return http
      .post<ProductModelAttribute>(`/product-models/${modelId}/attributes`, body)
      .then((res) => res.data);
  },
  update(
    modelId: string,
    attrId: string,
    body: UpdateProductModelAttributeBody
  ): Promise<ProductModelAttribute> {
    return http
      .patch<ProductModelAttribute>(`/product-models/${modelId}/attributes/${attrId}`, body)
      .then((res) => res.data);
  },
  toggle(modelId: string, attrId: string): Promise<ProductModelAttribute> {
    return http
      .post<ProductModelAttribute>(`/product-models/${modelId}/attributes/${attrId}/toggle`)
      .then((res) => res.data);
  },
  remove(modelId: string, attrId: string): Promise<void> {
    return http
      .delete<void>(`/product-models/${modelId}/attributes/${attrId}`)
      .then(() => undefined);
  },
  // 物理删除（真删行）：被控制标签引用时后端会 400 拦截（需先删标签）。
  purge(modelId: string, attrId: string): Promise<void> {
    return http
      .delete<void>(`/product-models/${modelId}/attributes/${attrId}/purge`)
      .then(() => undefined);
  }
};

// ============================================================
// Product Model Control Tags
// ============================================================

/** single/enum/cycle 下发属性；talkback 是 App 原生按住说话。 */
export type ControlTagKind = 'single' | 'enum' | 'cycle' | 'talkback';

export interface ControlTagOption {
  value: unknown;
  /** cycle 可选：一个档位同时写入的多个设备属性。 */
  fieldValues?: Record<string, unknown>;
  labelZh: string;
  labelEn?: string;
}

export interface ProductModelControlTag {
  id: string;
  productModelId: string;
  attributeId?: string | null;
  field?: string | null;
  valueType?: AttributeValueType | null;
  fieldNameZh?: string | null;
  fieldNameEn?: string;
  labelZh: string;
  labelEn?: string;
  tagKind: ControlTagKind;
  /** tagKind=single 时有值；enum/cycle 时为 null。 */
  targetValue: unknown;
  /** tagKind=enum/cycle 时的候选值；single/talkback 时为空数组。 */
  options: ControlTagOption[];
  icon?: string;
  /** 详情页标签文字颜色，#RRGGBB；旧数据为空时 App 回退 #1F2937。 */
  textColor?: string | null;
  blockWhenCleanEmpty?: boolean;
  blockWhenWasteFull?: boolean;
  confirmationRequired: boolean;
  confirmationTitleZh?: string | null;
  confirmationTitleEn?: string | null;
  confirmationMessageZh?: string | null;
  confirmationMessageEn?: string | null;
  enabled: boolean;
  sort: number;
  createdAt: string;
  updatedAt: string;
}

export interface ProductModelControlTagListParams {
  enabled?: boolean;
  q?: string;
  page?: number;
  size?: number;
}

/** single 传属性+targetValue；enum/cycle 传属性+options；talkback 不传属性和值。 */
export interface CreateProductModelControlTagBody {
  attributeId?: string;
  labelZh: string;
  labelEn?: string;
  tagKind?: ControlTagKind;
  targetValue?: unknown;
  options?: ControlTagOption[];
  icon?: string;
  textColor?: string;
  blockWhenCleanEmpty?: boolean;
  blockWhenWasteFull?: boolean;
  confirmationRequired?: boolean;
  confirmationTitleZh?: string;
  confirmationTitleEn?: string;
  confirmationMessageZh?: string;
  confirmationMessageEn?: string;
  sort?: number;
}

/** 切换 tagKind 时必须同时带上新形态所需的值，否则后端 400。 */
export type UpdateProductModelControlTagBody = Partial<{
  attributeId: string;
  labelZh: string;
  labelEn: string;
  tagKind: ControlTagKind;
  targetValue: unknown;
  options: ControlTagOption[];
  icon: string;
  textColor: string;
  blockWhenCleanEmpty?: boolean;
  blockWhenWasteFull?: boolean;
  confirmationRequired: boolean;
  confirmationTitleZh: string;
  confirmationTitleEn: string;
  confirmationMessageZh: string;
  confirmationMessageEn: string;
  sort: number;
  enabled: boolean;
}>;

// ============================================================
// Product Model Detail Blocks（详情页布局：整列表替换）
// ============================================================

export type DetailBlockType =
  | 'live_video'
  | 'metric_hero'
  | 'metric_grid'
  | 'metric_pair'
  | 'status_tile_list'
  | 'records_list';

export interface ProductModelDetailBlock {
  id: string;
  productModelId: string;
  blockType: DetailBlockType;
  sort: number;
  enabled: boolean;
  config: Record<string, unknown>;
  createdAt: string;
  updatedAt: string;
}

export interface DetailBlockInput {
  blockType: DetailBlockType;
  enabled?: boolean;
  config?: Record<string, unknown>;
}

export const productModelDetailBlockApi = {
  list(modelId: string): Promise<ProductModelDetailBlock[]> {
    return http
      .get<ProductModelDetailBlock[]>(`/product-models/${modelId}/detail-blocks`)
      .then((res) => res.data);
  },
  replace(modelId: string, blocks: DetailBlockInput[]): Promise<ProductModelDetailBlock[]> {
    return http
      .put<ProductModelDetailBlock[]>(`/product-models/${modelId}/detail-blocks`, {
        blocks
      })
      .then((res) => res.data);
  }
};

// ============================================================
// Product Model Catalog 发布（草稿/发布/版本/回滚）—— per-model
// ============================================================

export interface CatalogDraftStatus {
  dirty: boolean;
  currentVersionNo: number | null;
  lastDraftEditedAt: string | null;
  publishedAt: string | null;
}

export interface CatalogVersionSummary {
  versionNo: number;
  note: string | null;
  rolledBackFrom: number | null;
  createdBy: string;
  createdAt: string;
  isCurrent: boolean;
}

export interface CatalogPublishResult {
  versionNo: number;
  versionId: string;
}

export interface CatalogFieldChange {
  field: string;
  oldValue: string | null;
  newValue: string | null;
}
export interface CatalogDiffItem {
  label: string;
  fieldChanges: CatalogFieldChange[];
}
export interface CatalogCategoryDiff {
  category: string;
  label: string;
  added: CatalogDiffItem[];
  removed: CatalogDiffItem[];
  changed: CatalogDiffItem[];
}
export interface CatalogDiff {
  hasChanges: boolean;
  categories: CatalogCategoryDiff[];
}

export interface CatalogTransferIssue {
  level: 'info' | 'warning' | 'error';
  category: string;
  message: string;
}

export interface CatalogTransferPackage {
  formatVersion: number;
  sourceModelCode: string;
  sourceModelName: string;
  exportedAt: string;
  attributes: unknown[];
  indicatorConfig: Record<string, unknown> | null;
  snapshot: Record<string, unknown>;
  eventIngestPolicies?: ProductModelEventIngestPolicy[];
}

export interface CatalogTransferPreview {
  current: CatalogTransferPackage;
  incoming: CatalogTransferPackage;
  targetModelCode: string;
  issues: CatalogTransferIssue[];
}

export interface CatalogTransferImportResult {
  importedCategories: string[];
  issues: CatalogTransferIssue[];
}

export const productModelCatalogApi = {
  draftStatus(modelId: string): Promise<CatalogDraftStatus> {
    return http
      .get<CatalogDraftStatus>(`/product-models/${modelId}/catalog/draft-status`)
      .then((res) => res.data);
  },
  versions(modelId: string): Promise<CatalogVersionSummary[]> {
    return http
      .get<CatalogVersionSummary[]>(`/product-models/${modelId}/catalog/versions`)
      .then((res) => res.data);
  },
  publish(
    modelId: string,
    body: { note?: string; baseVersionNo: number | null }
  ): Promise<CatalogPublishResult> {
    return http
      .post<CatalogPublishResult>(`/product-models/${modelId}/catalog/publish`, body)
      .then((res) => res.data);
  },
  rollback(
    modelId: string,
    body: { targetVersionNo: number; note?: string; baseVersionNo: number | null }
  ): Promise<CatalogPublishResult> {
    return http
      .post<CatalogPublishResult>(`/product-models/${modelId}/catalog/rollback`, body)
      .then((res) => res.data);
  },
  diff(modelId: string, baseVersionNo?: number): Promise<CatalogDiff> {
    return http
      .get<CatalogDiff>(`/product-models/${modelId}/catalog/diff`, {
        params: baseVersionNo == null ? {} : { baseVersionNo }
      })
      .then((res) => res.data);
  },
  exportConfig(modelId: string): Promise<CatalogTransferPackage> {
    return http
      .get<CatalogTransferPackage>(`/product-models/${modelId}/catalog/transfer/export`)
      .then((res) => res.data);
  },
  previewImport(
    modelId: string,
    configPackage: CatalogTransferPackage
  ): Promise<CatalogTransferPreview> {
    return http
      .post<CatalogTransferPreview>(
        `/product-models/${modelId}/catalog/transfer/preview`,
        configPackage
      )
      .then((res) => res.data);
  },
  importConfig(
    modelId: string,
    body: {
      configPackage: CatalogTransferPackage;
      categories: string[];
      allowModelCodeMismatch?: boolean;
    }
  ): Promise<CatalogTransferImportResult> {
    return http
      .post<CatalogTransferImportResult>(
        `/product-models/${modelId}/catalog/transfer/import`,
        body
      )
      .then((res) => res.data);
  }
};

// ============================================================
// Product Model Shell Sections（设置页 shell 段：整列表替换）
// ============================================================

/** camera 段已于 V275 下线（C 端早已停止渲染，入口移至「我的宠物」Tab 与首页）。 */
export type ShellSectionType =
  | 'basic_info'
  | 'device_settings'
  | 'link_list'
  | 'notifications'
  | 'accessories'
  | 'storage';

export type ShellLinkActionType =
  | 'coming_soon'
  | 'open_url'
  | 'open_pdf'
  | 'open_markdown'
  | 'open_route';

export interface ShellLinkItem {
  icon?: string;
  label: string;
  /** @deprecated 编辑入口已下线；C 端仍渲染存量值，故保留类型以免存量数据在回传时丢失。 */
  subtitle?: string;
  action_type: ShellLinkActionType;
  action_payload?: Record<string, unknown>;
  [key: string]: unknown;
}

export interface ProductModelShellSection {
  id: string;
  productModelId: string;
  sectionType: ShellSectionType;
  sort: number;
  enabled: boolean;
  config: Record<string, unknown>;
  createdAt: string;
  updatedAt: string;
}

export interface ShellSectionInput {
  sectionType: ShellSectionType;
  enabled?: boolean;
  config?: Record<string, unknown>;
}

export const productModelShellSectionApi = {
  list(modelId: string): Promise<ProductModelShellSection[]> {
    return http
      .get<ProductModelShellSection[]>(`/product-models/${modelId}/shell-sections`)
      .then((res) => res.data);
  },
  replace(modelId: string, sections: ShellSectionInput[]): Promise<ProductModelShellSection[]> {
    return http
      .put<ProductModelShellSection[]>(`/product-models/${modelId}/shell-sections`, {
        sections
      })
      .then((res) => res.data);
  }
};

// ============================================================
// Product Model Settings Fields（device_settings 段下的 sections + fields）
// ============================================================

export type SettingsFieldKind =
  | 'toggle'
  | 'stepper'
  /** 拖动条；kind_config 与 stepper 同构（min/max/step/unit）。 */
  | 'slider'
  | 'time_of_day'
  | 'enum_picker'
  | 'cycle'
  | 'schedule'
  | 'schedule_times'
  | 'single_pet_auto_assign'
  | 'quiet_hours'
  | 'timed_switch'
  /**
   * 无值入口：点开进 App 内置页面（kind_config.route 为白名单键，须以 / 开头）。
   * 不写 settings shadow、不下发设备，scope 强制 cloud。
   */
  | 'navigate';

/** 字段作用域：device=下发设备（走 shadow）/ cloud=云端偏好（不下发，如宠物识别开关）。 */
export type SettingsFieldScope = 'device' | 'cloud';

/** kind=enum_picker/cycle 的 options 项。 */
export interface SettingsEnumOption {
  value: string;
  label: string;
  /** 选项英文 label（kind_config JSON 内兄弟键 label_en），可选；缺省回退中文。 */
  label_en?: string;
}

/** kind_config schema 联合类型；C 端按 kind 派发。 */
export type SettingsKindConfig =
  | Record<string, unknown>
  | {
      min: number;
      max: number;
      step: number;
      unit?: string;
      /** 设备协议值 = App 展示值 × 倍率；缺省 1。 */
      device_value_multiplier?: number;
    }
  | { options: SettingsEnumOption[] };

export interface ProductModelSettingsField {
  id: string;
  sectionId: string;
  productModelId: string;
  fieldKey: string;
  labelZh: string;
  labelEn?: string | null;
  descriptionZh?: string | null;
  descriptionEn?: string | null;
  kind: SettingsFieldKind;
  kindConfig: Record<string, unknown>;
  /** 作用域；缺省 device。 */
  scope: SettingsFieldScope;
  /** 默认值（标量，如 1 / "22:00"）；null 表示未设默认。 */
  defaultValue?: unknown;
  /** 离家模式取值（标量）；null=该字段不参与离家模式。 */
  awayValue?: unknown;
  /** 子分组名；同一分组内同名字段在 App 上收进同一个二级页入口，空=不归组。 */
  subgroupZh?: string | null;
  subgroupEn?: string | null;
  /** 用户变更此字段前是否弹出二次确认。 */
  confirmationRequired: boolean;
  /** 仅 fromValue -> toValue 的定向变更弹出确认。 */
  confirmationFromValue?: unknown;
  confirmationToValue?: unknown;
  confirmationTitleZh?: string | null;
  confirmationTitleEn?: string | null;
  confirmationMessageZh?: string | null;
  confirmationMessageEn?: string | null;
  sort: number;
  enabled: boolean;
  createdAt: string;
  updatedAt: string;
}

/** 分组展示方式：inline=平铺卡片；page=收成一行入口，点击进 App 二级页。仅影响渲染。 */
export type SettingsSectionDisplayMode = 'inline' | 'page' | 'page_auto';

export interface ProductModelSettingsSection {
  id: string;
  productModelId: string;
  code: string;
  titleZh: string;
  titleEn?: string | null;
  displayMode: SettingsSectionDisplayMode;
  sort: number;
  enabled: boolean;
  fields: ProductModelSettingsField[];
  createdAt: string;
  updatedAt: string;
}

export interface SettingsFieldInput {
  fieldKey: string;
  labelZh: string;
  labelEn?: string | null;
  descriptionZh?: string | null;
  descriptionEn?: string | null;
  kind: SettingsFieldKind;
  kindConfig?: Record<string, unknown>;
  /** 作用域；缺省 device。 */
  scope?: SettingsFieldScope;
  /** 默认值（标量）；null/省略表示不设默认。 */
  defaultValue?: unknown;
  /** 离家模式取值（标量）；null/省略=该字段不参与离家模式。 */
  awayValue?: unknown;
  /** 子分组名；同名字段在 App 上收成一个二级页入口，空/省略=不归组。 */
  subgroupZh?: string | null;
  subgroupEn?: string | null;
  confirmationRequired?: boolean;
  confirmationFromValue?: unknown;
  confirmationToValue?: unknown;
  confirmationTitleZh?: string | null;
  confirmationTitleEn?: string | null;
  confirmationMessageZh?: string | null;
  confirmationMessageEn?: string | null;
  enabled?: boolean;
}

export interface SettingsSectionInput {
  code: string;
  titleZh: string;
  titleEn?: string | null;
  /** 展示方式；缺省 inline。 */
  displayMode?: SettingsSectionDisplayMode;
  enabled?: boolean;
  fields?: SettingsFieldInput[];
}

export const productModelSettingsApi = {
  list(modelId: string): Promise<ProductModelSettingsSection[]> {
    return http
      .get<ProductModelSettingsSection[]>(`/product-models/${modelId}/settings-fields`)
      .then((res) => res.data);
  },
  replace(
    modelId: string,
    sections: SettingsSectionInput[]
  ): Promise<ProductModelSettingsSection[]> {
    return http
      .put<ProductModelSettingsSection[]>(`/product-models/${modelId}/settings-fields`, {
        sections
      })
      .then((res) => res.data);
  }
};

/** 与 C 端 DeviceRecord 字段对齐（Admin 预览 / 运维）。 */
export interface AdminDeviceRecord {
  id: string;
  deviceId: string;
  type: string;
  timestamp: string;
  payload?: Record<string, unknown>;
  typeLabel: string;
  summary: string;
  petId?: string;
  thumbUrl?: string;
  hasVideo: boolean;
  videoStatus?: string;
  videoUrl?: string;
  videoUrlExpiresAt?: string;
  recognitionStatus?: string;
}

export interface DetailBlockPreviewRecordsParams {
  deviceId: string;
  date?: string;
  /** 逗号分隔的 type_code，与 C 端 records API 一致 */
  types?: string;
  limit?: number;
  offset?: number;
}

export interface PreviewComputedBlock {
  blockType: string;
  config: Record<string, unknown>;
}

export interface PreviewComputedRequest {
  deviceId: string;
  blocks: PreviewComputedBlock[];
}

export const detailBlockPreviewApi = {
  listRecords(
    modelId: string,
    params: DetailBlockPreviewRecordsParams
  ): Promise<AdminDeviceRecord[]> {
    return http
      .get<AdminDeviceRecord[]>(`/product-models/${modelId}/detail-blocks/preview-records`, {
        params
      })
      .then((res) => res.data);
  },
  /** 用抽屉里的「未保存」block 列表，按 source.event_aggregate 计算 computed map。 */
  computed(modelId: string, body: PreviewComputedRequest): Promise<Record<string, unknown>> {
    return http
      .post<Record<string, unknown>>(
        `/product-models/${modelId}/detail-blocks/preview-computed`,
        body
      )
      .then((res) => res.data);
  }
};

export function adminDeviceRecordThumbUrl(recordId: string): string {
  return `/admin/api/device-records/${recordId}/thumb`;
}

/** 用户纠正设备事件宠物归属（审计，仅 Admin 只读）。 */
export interface AdminDeviceRecordCorrection {
  id: string;
  recordId: string;
  familyId: string;
  userId: string;
  originalPetId: string | null;
  correctedPetId: string;
  originalRecognitionStatus: string;
  newRecognitionStatus: string;
  createdAt: string;
}

export async function listDeviceRecordCorrections(
  recordId: string,
  limit = 50,
): Promise<AdminDeviceRecordCorrection[]> {
  const { data } = await http.get<AdminDeviceRecordCorrection[]>(
    `/device-records/${recordId}/corrections`,
    { params: { limit } },
  );
  return data;
}

/**
 * 布局配置（详情页 blocks / 设置页 shell sections）自定义图标预览 URL。
 *
 * config.icon 一个字段承载两种语义：内置 icon key 或上传图片路径，靠前缀区分。
 * 上传后存的是 C 端路径 /api/v1/layout-icons/xxx，admin 取不到，故改走同源代理。
 */
export function layoutIconPreviewUrl(icon?: string | null): string | null {
  if (!icon?.trim()) return null;
  const trimmed = icon.trim();
  if (trimmed.startsWith('http://') || trimmed.startsWith('https://')) {
    return trimmed;
  }
  const match = trimmed.match(/layout-icons\/([^/?#]+)/);
  if (match) {
    const adminBase = import.meta.env.VITE_ADMIN_BASE ?? '';
    return `${adminBase}/admin/api/layout-icons/${match[1]}`;
  }
  return null;
}

/** icon 值是否为上传图片（URL/路径），而非内置图标 key。 */
export function isLayoutIconImage(icon?: string | null): boolean {
  if (!icon?.trim()) return false;
  const trimmed = icon.trim();
  return (
    trimmed.startsWith('http://') ||
    trimmed.startsWith('https://') ||
    trimmed.startsWith('/') ||
    trimmed.includes('layout-icons/')
  );
}

/** 上传布局图标，返回可直接写入 config.icon 的路径。 */
export function uploadLayoutIcon(file: File): Promise<string> {
  const form = new FormData();
  form.append('file', file);
  return http
    .post<{ url: string }>('/product-model-layout-icons', form, {
      headers: { 'Content-Type': 'multipart/form-data' }
    })
    .then((res) => res.data.url);
}

/** 可控标签自定义图标预览 URL（本地上传存 C 端路径，Admin 走同源代理）。 */
export function controlTagIconPreviewUrl(icon?: string | null): string | null {
  if (!icon?.trim()) return null;
  const trimmed = icon.trim();
  if (trimmed.startsWith('http://') || trimmed.startsWith('https://')) {
    return trimmed;
  }
  const match = trimmed.match(/control-tag-icons\/([^/?#]+)(\?[^#]*)?(#.*)?$/);
  if (match) {
    const adminBase = import.meta.env.VITE_ADMIN_BASE ?? '';
    return `${adminBase}/admin/api/control-tag-icons/${match[1]}${match[2] ?? ''}${match[3] ?? ''}`;
  }
  return null;
}

/** icon 值是否为上传图片（URL/路径），而非图标 key。 */
export function isControlTagIconImage(icon?: string | null): boolean {
  if (!icon?.trim()) return false;
  const trimmed = icon.trim();
  return (
    trimmed.startsWith('http://') ||
    trimmed.startsWith('https://') ||
    trimmed.startsWith('/') ||
    trimmed.includes('control-tag-icons/')
  );
}

export const productModelControlTagApi = {
  list(
    modelId: string,
    params: ProductModelControlTagListParams = {}
  ): Promise<PageResponse<ProductModelControlTag>> {
    return http
      .get<PageResponse<ProductModelControlTag>>(`/product-models/${modelId}/control-tags`, {
        params
      })
      .then((res) => res.data);
  },
  get(modelId: string, tagId: string): Promise<ProductModelControlTag> {
    return http
      .get<ProductModelControlTag>(`/product-models/${modelId}/control-tags/${tagId}`)
      .then((res) => res.data);
  },
  create(modelId: string, body: CreateProductModelControlTagBody): Promise<ProductModelControlTag> {
    return http
      .post<ProductModelControlTag>(`/product-models/${modelId}/control-tags`, body)
      .then((res) => res.data);
  },
  update(
    modelId: string,
    tagId: string,
    body: UpdateProductModelControlTagBody
  ): Promise<ProductModelControlTag> {
    return http
      .patch<ProductModelControlTag>(`/product-models/${modelId}/control-tags/${tagId}`, body)
      .then((res) => res.data);
  },
  toggle(modelId: string, tagId: string): Promise<ProductModelControlTag> {
    return http
      .post<ProductModelControlTag>(`/product-models/${modelId}/control-tags/${tagId}/toggle`)
      .then((res) => res.data);
  },
  remove(modelId: string, tagId: string): Promise<void> {
    return http
      .delete<void>(`/product-models/${modelId}/control-tags/${tagId}`)
      .then(() => undefined);
  },
  // 物理删除（真删行）。
  purge(modelId: string, tagId: string): Promise<void> {
    return http
      .delete<void>(`/product-models/${modelId}/control-tags/${tagId}/purge`)
      .then(() => undefined);
  },
  uploadIcon(modelId: string, tagId: string, file: File): Promise<ProductModelControlTag> {
    const form = new FormData();
    form.append('file', file);
    return http
      .post<ProductModelControlTag>(
        `/product-models/${modelId}/control-tags/${tagId}/icon`,
        form,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      )
      .then((res) => res.data);
  }
};

// ============================================================
// Event Catalog
// ============================================================

export type EventCategory = 'behavior' | 'maintenance' | 'system';
export type EventSeverity = 'info' | 'warning' | 'critical';
/**
 * 通知策略：决定该事件落消息时的 ack_mode / resolve_signal（后端 EventMessageDispatcher 映射）。
 * - behavior_log：自动确认，仅存档，不进首页注意事项卡
 * - threshold_alert：手动确认，设备状态恢复时自动消除
 * - silence_alert：手动确认，关注事件再现时自动消除
 * - state_only_alert：仅状态恢复确认，禁止手动消除首页待办
 * - manual_alert：仅手动确认（默认）
 */
export type NotifyPolicy = 'behavior_log' | 'threshold_alert' | 'silence_alert' | 'manual_alert' | 'state_only_alert';

export interface EventCatalog {
  id: string;
  typeCode: string;
  displayNameZh: string;
  displayNameEn?: string;
  category: EventCategory;
  severity: EventSeverity;
  notifyPolicy: NotifyPolicy;
  icon?: string;
  needsPet: boolean;
  /** 是否要求媒体就绪后才向 App 发布；无本地录像时进入云录制等待。 */
  requiresMedia: boolean;
  payloadSchema?: string;
  aggregableTo?: string;
  /** 首页 Today SUM 聚合字段名（仅 aggregableTo ∈ food_g/water_ml 时有值，可逗号分隔做 fallback）。 */
  payloadField?: string;
  /** 该事件 payload 中体重字段名（仅 needsPet=true 时有值；预留给宠物 Tab 体重聚合）。 */
  weightField?: string;
  summaryTemplateZh?: string;
  /** 英文展示文案模板；为空时英文环境退化成只显示事件名。 */
  summaryTemplateEn?: string;
  /** up/event 自动确认规则 JSON；空对象表示关闭。 */
  autoResolveRule: string;
  enabled: boolean;
  sort: number;
  createdAt: string;
  updatedAt: string;
}

export interface EventCatalogListParams {
  enabled?: boolean;
  category?: EventCategory;
  q?: string;
  page?: number;
  size?: number;
}

export interface CreateEventCatalogBody {
  typeCode: string;
  displayNameZh: string;
  displayNameEn?: string;
  category: EventCategory;
  severity?: EventSeverity;
  notifyPolicy?: NotifyPolicy;
  icon?: string;
  needsPet?: boolean;
  requiresMedia?: boolean;
  payloadSchema?: string;
  aggregableTo?: string;
  payloadField?: string;
  weightField?: string;
  summaryTemplateZh?: string;
  summaryTemplateEn?: string;
  autoResolveRule?: string;
  sort?: number;
}

export type UpdateEventCatalogBody = Partial<{
  displayNameZh: string;
  displayNameEn: string;
  category: EventCategory;
  severity: EventSeverity;
  notifyPolicy: NotifyPolicy;
  icon: string;
  needsPet: boolean;
  requiresMedia: boolean;
  payloadSchema: string;
  aggregableTo: string;
  payloadField: string;
  weightField: string;
  summaryTemplateZh: string;
  summaryTemplateEn: string;
  autoResolveRule: string;
  sort: number;
  enabled: boolean;
}>;

export const eventCatalogApi = {
  list(params: EventCatalogListParams = {}): Promise<PageResponse<EventCatalog>> {
    return http
      .get<PageResponse<EventCatalog>>('/event-catalog', { params })
      .then((res) => res.data);
  },
  get(id: string): Promise<EventCatalog> {
    return http.get<EventCatalog>(`/event-catalog/${id}`).then((res) => res.data);
  },
  create(body: CreateEventCatalogBody): Promise<EventCatalog> {
    return http.post<EventCatalog>('/event-catalog', body).then((res) => res.data);
  },
  update(id: string, body: UpdateEventCatalogBody): Promise<EventCatalog> {
    return http.patch<EventCatalog>(`/event-catalog/${id}`, body).then((res) => res.data);
  },
  toggle(id: string): Promise<EventCatalog> {
    return http.post<EventCatalog>(`/event-catalog/${id}/toggle`).then((res) => res.data);
  },
  remove(id: string): Promise<void> {
    return http.delete<void>(`/event-catalog/${id}`).then(() => undefined);
  }
};

// ============================================================
// Community sensitive words
// ============================================================

export type SensitiveWordCategory = 'illegal' | 'harassment' | 'spam' | 'other';

export interface SensitiveWord {
  id: string;
  word: string;
  enabled: boolean;
  category?: string;
  remark?: string;
  createdAt: string;
  updatedAt: string;
}

export interface SensitiveWordListParams {
  enabled?: boolean;
  category?: string;
  q?: string;
  page?: number;
  size?: number;
}

export interface CreateSensitiveWordBody {
  word: string;
  category?: string;
  remark?: string;
}

export type UpdateSensitiveWordBody = Partial<{
  category: string;
  remark: string;
  enabled: boolean;
}>;

/** 机审聚合结论：pass 通过 / review 待复审 / block 拦截；null 表示无审核记录（本功能上线前老帖）。 */
export type AdminModerationSuggestion = 'pass' | 'review' | 'block';

export interface AdminCommunityPostSummary {
  id: string;
  authorUserId: string;
  authorName: string;
  textPreview: string;
  status: string;
  /** 可见性：public 公开 / private 私密（作者自己设的，与平台处置的 status 正交）。 */
  visibility?: string;
  /** 是否已评为精选（长期标记，无展示窗口）；列表据此显示「取消精选」。 */
  featured: boolean;
  likeCount: number;
  commentCount: number;
  reportCount: number;
  coverImageUrl?: string;
  moderationSuggestion?: AdminModerationSuggestion | null;
  moderationLabels?: string | null;
  createdAt: string;
}

export interface AdminCommunityPostDetail {
  id: string;
  authorUserId: string;
  authorName: string;
  text: string;
  petName?: string;
  deviceLabel?: string;
  location?: string;
  status: string;
  /** 是否已评为精选（长期标记，无展示窗口）。 */
  featured: boolean;
  likeCount: number;
  commentCount: number;
  reportCount: number;
  imageUrls: string[];
  reports: {
    id: string;
    reporterUserId: string;
    reasonType: string;
    detail?: string;
    createdAt: string;
  }[];
  moderationSuggestion?: AdminModerationSuggestion | null;
  moderationLabels?: string | null;
  moderationScore?: number | null;
  moderatedAt?: string | null;
  createdAt: string;
}

export type AdminCommunityPostSort = 'time' | 'likes' | 'comments' | 'reports';

export interface AdminCommunityPostListParams {
  status?: string;
  /** 机审结论筛选：pass/review/block（如「待复审」批量捞）。 */
  moderation?: AdminModerationSuggestion;
  q?: string;
  /** 发布时间下界（yyyy-MM-dd，含当日，按 UTC 自然日）。 */
  from?: string;
  /** 发布时间上界（yyyy-MM-dd，含当日，按 UTC 自然日）。 */
  to?: string;
  /** 仅看某标签/话题下的帖子（标签 id）。 */
  tagId?: string;
  sort?: AdminCommunityPostSort;
  order?: 'asc' | 'desc';
  page?: number;
  size?: number;
}

export const communityPostApi = {
  list(params: AdminCommunityPostListParams = {}): Promise<PageResponse<AdminCommunityPostSummary>> {
    return http
      .get<PageResponse<AdminCommunityPostSummary>>('/community/posts', { params })
      .then((res) => res.data);
  },
  get(id: string): Promise<AdminCommunityPostDetail> {
    return http.get<AdminCommunityPostDetail>(`/community/posts/${id}`).then((res) => res.data);
  },
  /**
   * 改帖子状态。
   *
   * `removed` 只在帖子处于 `reviewing`（机审疑似待复审）时被后端接受——那是复审
   * 不通过的出口；普通帖子的删除另有入口，不从这里绕。
   */
  updateStatus(
    id: string,
    status: 'published' | 'hidden' | 'removed',
    reason?: string
  ): Promise<void> {
    return http
      .patch<void>(`/community/posts/${id}/status`, { status, reason })
      .then(() => undefined);
  },
  /**
   * 设置 / 取消帖子的「精选」长期标记：`featured=true` 设为精选，`false` 取消。
   *
   * 与下面的「内容精选奖励」是**两个独立动作**——打标只影响推荐排序加权，不发积分。
   * 已删除（removed）的帖子后端会拒绝（400，body 带 `message`）。
   */
  setFeatured(id: string, featured: boolean): Promise<void> {
    return http
      .patch<void>(`/community/posts/${id}/featured`, { featured })
      .then(() => undefined);
  },
  /**
   * 「评为精选并奖励」：给帖子作者发固定积分（默认 300）。
   *
   * 与 `setFeatured` 打标是两件事——打标不发积分，奖励必须显式触发并留审计；同一帖子
   * 重复调用不会重复入账，`replayed=true` 表示此前已发过。
   */
  grantContentReward(id: string): Promise<ContentRewardResult> {
    return http
      .post<ContentRewardResult>(`/community/posts/${id}/content-reward`)
      .then((res) => res.data);
  }
};

/** 内容精选奖励发放结果。 */
export interface ContentRewardResult {
  postId: string;
  authorUserId: string;
  points: number;
  transactionId: string;
  balanceAfter: number;
  replayed: boolean;
}

// ===== 奖励审核（D17：建议采纳 / 有效问题反馈） =====

/** 反馈奖励结论类型；两者独立、可叠加，各 500–5000。 */
export type FeedbackConclusionType = 'SUGGESTION_ADOPTED' | 'VALID_ISSUE';

/** 奖励审核列表行（含反馈与用户摘要）。 */
export interface RewardOpsConclusion {
  id: string;
  feedbackId: string;
  conclusionType: FeedbackConclusionType;
  points: number;
  issueGroupId: string;
  issueGroupTitle: string;
  reason: string;
  decidedBy: string;
  decidedAt: string;
  rewardTransactionId: string;
  newInformationOverride: boolean;
  userId: string | null;
  userName: string | null;
  userPhone: string | null;
  category: string | null;
  bodyPreview: string | null;
}

/** 某条反馈已有的结论（反馈详情页用）。 */
export interface FeedbackConclusion {
  id: string;
  feedbackId: string;
  conclusionType: FeedbackConclusionType;
  points: number;
  issueGroupId: string;
  reason: string;
  decidedBy: string;
  decidedAt: string;
  transactionId: string;
  newInformationOverride: boolean;
}

/** 奖励审核列表页：`totalPoints` 是所有已发放奖励的累计积分（预算汇总）。 */
export interface RewardOpsConclusionPage {
  items: RewardOpsConclusion[];
  total: number;
  page: number;
  size: number;
  totalPoints: number;
}

export interface RewardOpsIssueGroup {
  id: string;
  title: string;
  conclusionCount: number;
  createdAt: string;
}

export interface ConcludeFeedbackBody {
  conclusionType: FeedbackConclusionType;
  points: number;
  /** 关联已有问题组；为空表示新建（此时 newGroupTitle 必填）。 */
  issueGroupId?: string;
  newGroupTitle?: string;
  reason: string;
  /** 「本条提供了新信息」——覆盖问题组去重，需要高额发放权限。 */
  overrideNewInformation: boolean;
}

export interface ConcludeFeedbackResult {
  conclusionType: FeedbackConclusionType;
  feedbackId: string;
  points: number;
  issueGroupId: string;
  issueGroupTitle: string;
  reason: string;
  decidedBy: string;
  decidedAt: string;
  transactionId: string | null;
  balanceAfter: number | null;
  newInformationOverride: boolean;
  replayed: boolean;
}

/**
 * 奖励审核（D17）。
 *
 * 权限：`reward:ops:view` 读；`reward:ops:grant` 发 500–1000；
 * `reward:ops:grant:high` 发 1001–5000，以及勾选「确认为新信息」覆盖问题组去重。
 */
export const rewardOpsApi = {
  list(page = 1, size = 20): Promise<RewardOpsConclusionPage> {
    return http
      .get<RewardOpsConclusionPage>('/reward-ops/conclusions', { params: { page, size } })
      .then((res) => res.data);
  },
  forFeedback(feedbackId: string): Promise<FeedbackConclusion[]> {
    return http
      .get<FeedbackConclusion[]>(`/reward-ops/feedbacks/${feedbackId}/conclusions`)
      .then((res) => res.data);
  },
  issueGroups(q?: string): Promise<RewardOpsIssueGroup[]> {
    return http
      .get<RewardOpsIssueGroup[]>('/reward-ops/issue-groups', { params: { q } })
      .then((res) => res.data);
  },
  conclude(feedbackId: string, body: ConcludeFeedbackBody): Promise<ConcludeFeedbackResult> {
    return http
      .post<ConcludeFeedbackResult>(`/reward-ops/feedbacks/${feedbackId}/conclusions`, body)
      .then((res) => res.data);
  }
};

export type CommunityTagSource = 'official' | 'ugc';
export type CommunityTagStatus = 'active' | 'pending' | 'blocked';

export interface CommunityTag {
  id: string;
  name: string;
  source: CommunityTagSource;
  status: CommunityTagStatus;
  sort: number;
  postCount: number;
  /** 官方话题封面图 URL；ugc 标签为空。 */
  coverUrl?: string | null;
  /** 官方话题主页简介；ugc 标签为空。 */
  intro?: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface CommunityTagListParams {
  status?: CommunityTagStatus;
  source?: CommunityTagSource;
  q?: string;
  page?: number;
  size?: number;
}

/** 新建 / 编辑官方话题的入参。 */
export interface OfficialTopicInput {
  name: string;
  coverUrl?: string | null;
  intro?: string | null;
  sort: number;
}

export const communityTagApi = {
  list(params: CommunityTagListParams = {}): Promise<PageResponse<CommunityTag>> {
    return http
      .get<PageResponse<CommunityTag>>('/community/tags', { params })
      .then((res) => res.data);
  },
  approve(id: string): Promise<CommunityTag> {
    return http.post<CommunityTag>(`/community/tags/${id}/approve`).then((res) => res.data);
  },
  reject(id: string): Promise<CommunityTag> {
    return http.post<CommunityTag>(`/community/tags/${id}/reject`).then((res) => res.data);
  },
  promote(id: string, sort: number): Promise<CommunityTag> {
    return http
      .post<CommunityTag>(`/community/tags/${id}/promote`, { sort })
      .then((res) => res.data);
  },
  /** 新建官方话题（带封面 + 简介）。 */
  createTopic(input: OfficialTopicInput): Promise<CommunityTag> {
    return http.post<CommunityTag>('/community/topics', input).then((res) => res.data);
  },
  /** 编辑官方话题。 */
  updateTopic(id: string, input: OfficialTopicInput): Promise<CommunityTag> {
    return http.put<CommunityTag>(`/community/topics/${id}`, input).then((res) => res.data);
  },
  /** 上传话题封面图，返回 C 端公开 URL。 */
  uploadCover(file: File): Promise<{ url: string }> {
    const form = new FormData();
    form.append('file', file);
    return http
      .post<{ url: string }>('/community/topics/cover', form, {
        headers: { 'Content-Type': 'multipart/form-data' }
      })
      .then((res) => res.data);
  }
};

// Community reported users + ban
export interface AdminReportedUser {
  userId: string;
  name: string;
  avatarUrl: string;
  reportCount: number;
  lastReportedAt: string;
  banned: boolean;
  bannedUntil: string | null;
}

export interface AdminUserReportItem {
  id: string;
  reporterUserId: string;
  reporterName: string;
  reasonType: string;
  detail: string | null;
  createdAt: string;
  imageUrls: string[];
}

export interface AdminReportedUserDetail {
  userId: string;
  name: string;
  avatarUrl: string;
  banned: boolean;
  bannedUntil: string | null;
  banReason: string | null;
  reports: AdminUserReportItem[];
}

export const communityUserApi = {
  /** 被举报用户聚合列表（按最近举报时间倒序分页）。 */
  listReported(
    params: { page?: number; size?: number } = {}
  ): Promise<PageResponse<AdminReportedUser>> {
    return http
      .get<PageResponse<AdminReportedUser>>('/community/users/reported', { params })
      .then((res) => res.data);
  },
  /** 某被举报用户详情（举报明细 + 证据图 + 封禁状态）。 */
  detail(userId: string): Promise<AdminReportedUserDetail> {
    return http
      .get<AdminReportedUserDetail>(`/community/users/${userId}`)
      .then((res) => res.data);
  },
  /** 封禁用户社区功能 N 天。 */
  ban(userId: string, durationDays: number, reason: string): Promise<void> {
    return http
      .post(`/community/users/${userId}/ban`, { durationDays, reason })
      .then(() => undefined);
  },
  /** 提前解封。 */
  lift(userId: string): Promise<void> {
    return http.delete(`/community/users/${userId}/ban`).then(() => undefined);
  }
};

// Chat reported groups（群聊举报审核）
export interface AdminReportedGroup {
  conversationId: string;
  title: string;
  avatarUrl: string | null;
  memberCount: number;
  reportCount: number;
  lastReportedAt: string;
}

export interface AdminGroupReportItem {
  id: string;
  reporterUserId: string;
  reporterName: string;
  reasonType: string;
  detail: string | null;
  createdAt: string;
  status: string;
  imageUrls: string[];
}

export interface AdminReportedGroupDetail {
  conversationId: string;
  title: string;
  avatarUrl: string | null;
  ownerId: string | null;
  memberCount: number;
  reports: AdminGroupReportItem[];
}

export const chatGroupReportApi = {
  /** 被举报群聚合列表（status=PENDING 默认 / DISMISSED）。 */
  listReported(
    params: { status?: string; page?: number; size?: number } = {}
  ): Promise<PageResponse<AdminReportedGroup>> {
    return http
      .get<PageResponse<AdminReportedGroup>>('/chat/group-reports', { params })
      .then((res) => res.data);
  },
  /** 某被举报群详情（群信息 + 举报明细 + 证据图）。 */
  detail(conversationId: string): Promise<AdminReportedGroupDetail> {
    return http
      .get<AdminReportedGroupDetail>(`/chat/group-reports/${conversationId}`)
      .then((res) => res.data);
  },
  /** 忽略某群的全部待处理举报（群保留）。 */
  dismiss(conversationId: string): Promise<void> {
    return http.post(`/chat/group-reports/${conversationId}/dismiss`).then(() => undefined);
  },
  /** 强制解散违规群。 */
  dissolve(conversationId: string): Promise<void> {
    return http.post(`/chat/group-reports/${conversationId}/dissolve`).then(() => undefined);
  }
};

export const sensitiveWordApi = {
  list(params: SensitiveWordListParams = {}): Promise<PageResponse<SensitiveWord>> {
    return http
      .get<PageResponse<SensitiveWord>>('/community/sensitive-words', { params })
      .then((res) => res.data);
  },
  get(id: string): Promise<SensitiveWord> {
    return http.get<SensitiveWord>(`/community/sensitive-words/${id}`).then((res) => res.data);
  },
  create(body: CreateSensitiveWordBody): Promise<SensitiveWord> {
    return http.post<SensitiveWord>('/community/sensitive-words', body).then((res) => res.data);
  },
  update(id: string, body: UpdateSensitiveWordBody): Promise<SensitiveWord> {
    return http
      .patch<SensitiveWord>(`/community/sensitive-words/${id}`, body)
      .then((res) => res.data);
  },
  toggle(id: string): Promise<SensitiveWord> {
    return http
      .post<SensitiveWord>(`/community/sensitive-words/${id}/toggle`)
      .then((res) => res.data);
  },
  remove(id: string): Promise<void> {
    return http.delete<void>(`/community/sensitive-words/${id}`).then(() => undefined);
  }
};

// ============================================================
// Community Search Keywords（搜索热词：C 端「猜你想搜」榜单治理）
// ============================================================

export interface AdminSearchKeyword {
  keyword: string;
  /** 当期热度＝近 N 天去重用户数，热搜榜真正的排序依据。 */
  hotUsers: number;
  /** 全历史累计被搜次数，仅用于输入联想排序。 */
  searchCount: number;
  lastSearchedAt?: string;
  createdAt?: string;
  blocked: boolean;
  blockedAt?: string;
  /** 命中当前敏感词库：读取侧已自动过滤，无需再拉黑。 */
  hitsDictionary: boolean;
}

export interface SearchKeywordListParams {
  q?: string;
  blocked?: boolean;
  page?: number;
  size?: number;
}

/** 比通用分页多一个 hotWindowDays：「当期热度」是近 N 天去重人数，N 由后端配置。 */
export interface SearchKeywordPageResponse extends PageResponse<AdminSearchKeyword> {
  hotWindowDays: number;
}

export const searchKeywordApi = {
  list(params: SearchKeywordListParams = {}): Promise<SearchKeywordPageResponse> {
    return http
      .get<SearchKeywordPageResponse>('/community/search-keywords', { params })
      .then((res) => res.data);
  },
  block(keyword: string): Promise<void> {
    return http.post<void>('/community/search-keywords/block', { keyword }).then(() => undefined);
  },
  unblock(keyword: string): Promise<void> {
    return http.post<void>('/community/search-keywords/unblock', { keyword }).then(() => undefined);
  },
  remove(keyword: string): Promise<void> {
    return http
      .delete<void>('/community/search-keywords', { params: { keyword } })
      .then(() => undefined);
  }
};

// ============================================================
// Product Model Event Notifications（事件通知候选清单）
// ============================================================

export interface ProductModelEventNotification {
  id: string;
  productModelId: string;
  /** 被监听的事件类型（设备上报）。 */
  eventType: string;
  /** 输出事件：型号内唯一的用户开关与计数键；主监听消息内容取它的字典条目。 */
  outputEventType: string;
  /** 共用本条用户开关的附加监听事件。 */
  additionalEventTypes: string[];
  /** 该通知形成待办时的灯态等级；mandatory 高于 todo。 */
  indicatorLevel: IndicatorLevel;
  defaultEnabled: boolean;
  /** C 端通知设置项中文标题；为空回退输出事件字典中文名。 */
  titleZh?: string;
  /** C 端通知设置项英文标题；为空回退中文标题/输出事件字典英文名。 */
  titleEn?: string;
  descriptionZh?: string;
  /** C 端英文环境的副标题；为空回退中文。 */
  descriptionEn?: string;
  sort: number;
  enabled: boolean;
  notifyMode: EventNotifyMode;
  resetEventType?: string;
  defaultTriggerCount?: number;
  minTriggerCount?: number;
  maxTriggerCount?: number;
  solvable: boolean;
  solutionVideoId?: string;
  solutionText?: string;
  /** 英文解决办法文案；为空时英文环境回退中文。 */
  solutionTextEn?: string;
  createdAt: string;
  updatedAt: string;
}

export type EventNotifyMode = 'immediate' | 'count_threshold';
export type IndicatorLevel = 'todo' | 'mandatory';
export type IndicatorMode = 'static' | 'breath' | 'blink';

export interface EventNotificationInput {
  eventType: string;
  /** 留空表示「监听什么就通知什么」，后端回落到 eventType。 */
  outputEventType?: string;
  additionalEventTypes?: string[];
  indicatorLevel?: IndicatorLevel;
  defaultEnabled?: boolean;
  titleZh?: string;
  titleEn?: string;
  descriptionZh?: string;
  descriptionEn?: string;
  enabled?: boolean;
  notifyMode?: EventNotifyMode;
  resetEventType?: string;
  defaultTriggerCount?: number;
  minTriggerCount?: number;
  maxTriggerCount?: number;
  solvable?: boolean;
  solutionVideoId?: string;
  solutionText?: string;
  solutionTextEn?: string;
}

export const productModelEventNotificationApi = {
  list(modelId: string): Promise<ProductModelEventNotification[]> {
    return http
      .get<ProductModelEventNotification[]>(`/product-models/${modelId}/event-notifications`)
      .then((res) => res.data);
  },
  replace(
    modelId: string,
    items: EventNotificationInput[]
  ): Promise<ProductModelEventNotification[]> {
    return http
      .put<ProductModelEventNotification[]>(`/product-models/${modelId}/event-notifications`, {
        items
      })
      .then((res) => res.data);
  }
};

export interface ProductModelEventIngestPolicy {
  eventType: string;
  needsPet: boolean | null;
  requiresMedia: boolean | null;
}

export const productModelEventIngestPolicyApi = {
  list(modelId: string): Promise<ProductModelEventIngestPolicy[]> {
    return http.get<ProductModelEventIngestPolicy[]>(`/product-models/${modelId}/event-ingest-policies`)
      .then((res) => res.data);
  },
  replace(modelId: string, items: ProductModelEventIngestPolicy[]): Promise<ProductModelEventIngestPolicy[]> {
    return http.put<ProductModelEventIngestPolicy[]>(`/product-models/${modelId}/event-ingest-policies`, items)
      .then((res) => res.data);
  }
};

export interface IndicatorProfile {
  color: string;
  mode: IndicatorMode;
  freq?: number;
}

export interface ProductModelIndicatorConfig {
  productModelId: string;
  enabled: boolean;
  normal: IndicatorProfile;
  todo: IndicatorProfile;
  mandatory: IndicatorProfile;
  createdAt?: string;
  updatedAt?: string;
}

export interface ProductModelIndicatorConfigInput {
  enabled: boolean;
  normal: IndicatorProfile;
  todo: IndicatorProfile;
  mandatory: IndicatorProfile;
}

export const productModelIndicatorConfigApi = {
  get(modelId: string): Promise<ProductModelIndicatorConfig> {
    return http
      .get<ProductModelIndicatorConfig>(`/product-models/${modelId}/indicator-config`)
      .then((res) => res.data);
  },
  update(
    modelId: string,
    input: ProductModelIndicatorConfigInput
  ): Promise<ProductModelIndicatorConfig> {
    return http
      .put<ProductModelIndicatorConfig>(`/product-models/${modelId}/indicator-config`, input)
      .then((res) => res.data);
  }
};

// ============================================================
// Product Model Accessories（配件耗材更换提醒）
// ============================================================

export type AccessoryTriggerMode = 'event_count' | 'day_count';

export interface ProductModelAccessory {
  id: string;
  productModelId: string;
  code: string;
  nameZh: string;
  nameEn?: string;
  imageUrl?: string;
  descriptionZh: string;
  descriptionEn?: string;
  triggerMode: AccessoryTriggerMode;
  eventType?: string;
  triggerCount?: number;
  triggerDays?: number;
  sort: number;
  enabled: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface ProductModelAccessoryInput {
  code: string;
  nameZh: string;
  nameEn?: string;
  imageUrl?: string;
  descriptionZh: string;
  descriptionEn?: string;
  triggerMode: AccessoryTriggerMode;
  eventType?: string;
  triggerCount?: number;
  triggerDays?: number;
  enabled?: boolean;
}

export const productModelAccessoryApi = {
  list(modelId: string): Promise<ProductModelAccessory[]> {
    return http
      .get<ProductModelAccessory[]>(`/product-models/${modelId}/accessories`)
      .then((res) => res.data);
  },
  replace(modelId: string, items: ProductModelAccessoryInput[]): Promise<ProductModelAccessory[]> {
    return http
      .put<ProductModelAccessory[]>(`/product-models/${modelId}/accessories`, { items })
      .then((res) => res.data);
  },
  uploadImage(modelId: string, file: File): Promise<string> {
    const form = new FormData();
    form.append('file', file);
    return http
      .post<{ url: string }>(`/product-models/${modelId}/accessories/images`, form, {
        headers: { 'Content-Type': 'multipart/form-data' }
      })
      .then((res) => res.data.url);
  }
};

/** 将 C 端配件图片路径转换为管理后台可匿名读取的同源预览地址。 */
export function accessoryImagePreviewUrl(imageUrl?: string | null): string | null {
  const value = imageUrl?.trim();
  if (!value) return null;
  if (value.startsWith('http://') || value.startsWith('https://')) return value;
  const match = value.match(/accessory-images\/([^/?#]+)\/([^/?#]+)/);
  if (!match) return value;
  const adminBase = import.meta.env.VITE_ADMIN_BASE ?? '';
  return `${adminBase}/admin/api/accessory-images/${match[1]}/${match[2]}`;
}

// ============================================================
// ============================================================
// 型号配网指引（App 手动添加设备：选完型号先展示的说明页）
// ============================================================

export interface PairingGuideStep {
  zh?: string | null;
  en?: string | null;
}

export interface ProductModelPairingGuide {
  productModelId: string;
  videoResourceId?: string | null;
  /** 读接口会带出视频资源库里的公开相对路径；保存时只传 videoResourceId。 */
  videoUrl?: string | null;
  titleZh?: string | null;
  titleEn?: string | null;
  steps: PairingGuideStep[];
  enabled: boolean;
  updatedAt?: string | null;
}

export interface SavePairingGuideBody {
  videoResourceId: string | null;
  titleZh?: string | null;
  titleEn?: string | null;
  steps: PairingGuideStep[];
  enabled: boolean;
}

export const productModelPairingGuideApi = {
  /** 该型号的指引；后端未配置时返回 204 → 这里归一成 null。 */
  get(modelId: string): Promise<ProductModelPairingGuide | null> {
    return http
      .get<ProductModelPairingGuide | ''>(`/product-models/${modelId}/pairing-guide`)
      .then((res) => (res.status === 204 || !res.data ? null : (res.data as ProductModelPairingGuide)));
  },
  save(modelId: string, body: SavePairingGuideBody): Promise<ProductModelPairingGuide> {
    return http
      .put<ProductModelPairingGuide>(`/product-models/${modelId}/pairing-guide`, body)
      .then((res) => res.data);
  },
  remove(modelId: string): Promise<void> {
    return http.delete(`/product-models/${modelId}/pairing-guide`).then(() => undefined);
  }
};

// Video Resources（视频资源库：统一管理教程/帮助视频）
// ============================================================

export interface VideoResource {
  id: string;
  title: string;
  url: string;
  contentType?: string;
  sizeBytes?: number;
  createdAt: string;
  updatedAt: string;
}

export const videoResourceApi = {
  list(): Promise<VideoResource[]> {
    return http.get<VideoResource[]>('/video-resources').then((res) => res.data);
  },
  upload(file: File, title: string): Promise<VideoResource> {
    const form = new FormData();
    form.append('file', file);
    form.append('title', title);
    return http
      .post<VideoResource>('/video-resources', form, {
        headers: { 'Content-Type': 'multipart/form-data' }
      })
      .then((res) => res.data);
  },
  rename(id: string, title: string): Promise<VideoResource> {
    return http.put<VideoResource>(`/video-resources/${id}`, { title }).then((res) => res.data);
  },
  replaceFile(id: string, file: File): Promise<VideoResource> {
    const form = new FormData();
    form.append('file', file);
    return http.put<VideoResource>(`/video-resources/${id}/file`, form, {
      headers: { 'Content-Type': 'multipart/form-data' }
    }).then((res) => res.data);
  },
  remove(id: string): Promise<void> {
    return http.delete(`/video-resources/${id}`).then(() => undefined);
  }
};

// ============================================================
// Product Model Alert Rules（沉默告警规则候选清单）
// ============================================================

export type AlertRuleKind = 'silence';
export type AlertSeverity = 'info' | 'warning' | 'critical';
export type AlertDisplayPriority = 'P0' | 'P1' | 'P2' | 'P3';

export interface ProductModelAlertRule {
  id: string;
  productModelId: string;
  ruleCode: string;
  ruleKind: AlertRuleKind;
  titleZh: string;
  descriptionZh?: string;
  watchedEventTypes: string[];
  defaultSilenceHours: number;
  minSilenceHours: number;
  maxSilenceHours: number;
  defaultEnabled: boolean;
  defaultSeverity: AlertSeverity;
  defaultDisplayPriority: AlertDisplayPriority;
  titleTemplateZh?: string;
  bodyTemplateZh?: string;
  sort: number;
  enabled: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface AlertRuleInput {
  ruleCode: string;
  ruleKind?: AlertRuleKind;
  titleZh: string;
  descriptionZh?: string;
  watchedEventTypes: string[];
  defaultSilenceHours: number;
  minSilenceHours: number;
  maxSilenceHours: number;
  defaultEnabled?: boolean;
  defaultSeverity?: AlertSeverity;
  defaultDisplayPriority?: AlertDisplayPriority;
  titleTemplateZh?: string;
  bodyTemplateZh?: string;
  enabled?: boolean;
}

export const productModelAlertRuleApi = {
  list(modelId: string): Promise<ProductModelAlertRule[]> {
    return http
      .get<ProductModelAlertRule[]>(`/product-models/${modelId}/alert-rules`)
      .then((res) => res.data);
  },
  replace(modelId: string, items: AlertRuleInput[]): Promise<ProductModelAlertRule[]> {
    return http
      .put<ProductModelAlertRule[]>(`/product-models/${modelId}/alert-rules`, {
        items
      })
      .then((res) => res.data);
  }
};

// ============================================================
// User Feedback（投诉建议）
// ============================================================

export interface AdminUserFeedbackSummary {
  id: string;
  userId: string;
  userName?: string;
  userPhone?: string;
  category: 'complaint' | 'suggestion';
  bodyPreview: string;
  status: 'pending' | 'reviewed' | 'resolved';
  /** 用户上传的截图张数（0~3），详情里可看图 */
  imageCount: number;
  /** 是否带语音留言，详情里可播放 */
  hasVoice: boolean;
  createdAt: string;
  reviewedAt?: string;
}

export interface AdminUserFeedbackDetail {
  id: string;
  userId: string;
  userName?: string;
  userPhone?: string;
  category: 'complaint' | 'suggestion';
  appVersion?: string;
  deviceLabel?: string;
  status: 'pending' | 'reviewed' | 'resolved';
  adminNote?: string;
  /** 反馈正文；纯语音反馈时为空（正文与语音至少有其一） */
  body?: string;
  /** 用户上传的截图张数（0~3）；截图属私密媒体，取图走 userFeedbackApi.imageBlobUrl */
  imageCount: number;
  /** 是否带语音留言；语音属私密媒体，取音频走 userFeedbackApi.voiceBlobUrl */
  hasVoice: boolean;
  /** 语音时长毫秒（客户端上报，仅供展示） */
  voiceDurationMs?: number;
  createdAt: string;
  reviewedAt?: string;
}

export interface AdminUserFeedbackListParams {
  status?: string;
  q?: string;
  page?: number;
  size?: number;
}

export const userFeedbackApi = {
  list(
    params: AdminUserFeedbackListParams = {}
  ): Promise<PageResponse<AdminUserFeedbackSummary>> {
    return http
      .get<PageResponse<AdminUserFeedbackSummary>>('/user-feedback', { params })
      .then((res) => res.data);
  },
  get(id: string): Promise<AdminUserFeedbackDetail> {
    return http.get<AdminUserFeedbackDetail>(`/user-feedback/${id}`).then((res) => res.data);
  },
  updateStatus(
    id: string,
    body: { status: 'pending' | 'reviewed' | 'resolved'; adminNote?: string }
  ): Promise<void> {
    return http.patch<void>(`/user-feedback/${id}`, body).then(() => undefined);
  },
  /**
   * 取第 index 张反馈截图并转成 blob URL。
   *
   * 截图是私密媒体：后端不下发对象键/直链，只有本鉴权端点能读（需 feedback:view），
   * 故不能用 `<img src>`（带不上 admin JWT），必须 XHR 取字节再 createObjectURL。
   * 调用方负责在关闭时 revokeObjectURL。
   */
  async imageBlobUrl(id: string, index: number): Promise<string> {
    const res = await http.get<Blob>(`/user-feedback/${id}/images/${index}`, {
      responseType: 'blob'
    });
    return URL.createObjectURL(res.data);
  },
  /**
   * 取语音留言并转成 blob URL 喂给 <audio>。
   *
   * 同截图：语音是私密媒体，只有本鉴权端点能读（需 feedback:view），
   * `<audio src>` 带不上 admin JWT，必须 XHR 取字节再 createObjectURL。
   * 调用方负责在关闭时 revokeObjectURL。
   */
  async voiceBlobUrl(id: string): Promise<string> {
    const res = await http.get<Blob>(`/user-feedback/${id}/voice`, { responseType: 'blob' });
    return URL.createObjectURL(res.data);
  }
};

// ============================================================
// 消息中心：后台给 C 端发系统消息（公告）
// ============================================================

export type AnnouncementTargetType = 'all' | 'family' | 'user';

export interface AdminAnnouncement {
  id: string;
  title: string;
  body: string;
  targetType: AnnouncementTargetType;
  targetId: string | null;
  severity: string;
  pushRequested: boolean;
  pushSent: boolean;
  createdBy: string | null;
  createdAt: string;
  /** 富文本 doc JSON 字符串（tiptap）；空 `{}` 表示仅用 body 纯文本。 */
  content: string;
  /** 封面图 URL（C 端 canonical 路径或外链）；无封面为 null。 */
  coverUrl: string | null;
}

export interface AnnouncementListParams {
  q?: string;
  page?: number;
  size?: number;
}

export interface SendAnnouncementBody {
  title: string;
  body?: string;
  /** 富文本 doc JSON 字符串（tiptap）。 */
  content?: string;
  /** 封面图 URL（可选，C 端 canonical 路径）。 */
  coverUrl?: string;
  targetType: AnnouncementTargetType;
  targetId?: string | null;
  severity?: string;
  push: boolean;
}

export interface AnnouncementTargetOption {
  id: string;
  label: string;
}

export interface ContentMediaUploaded {
  url: string;
  kind: 'image' | 'video';
}

/** 富文本编辑器「内容媒体」上传（图片 + 视频）。公告与社区文章共用。 */
export const contentMediaApi = {
  upload(file: File): Promise<ContentMediaUploaded> {
    const form = new FormData();
    form.append('file', file);
    return http
      .post<ContentMediaUploaded>('/content-media', form, {
        headers: { 'Content-Type': 'multipart/form-data' }
      })
      .then((res) => res.data);
  }
};

export const announcementApi = {
  list(params: AnnouncementListParams = {}): Promise<PageResponse<AdminAnnouncement>> {
    return http
      .get<PageResponse<AdminAnnouncement>>('/announcements', { params })
      .then((res) => res.data);
  },
  send(body: SendAnnouncementBody): Promise<AdminAnnouncement> {
    return http.post<AdminAnnouncement>('/announcements', body).then((res) => res.data);
  },
  remove(id: string): Promise<void> {
    return http.delete<void>(`/announcements/${id}`).then(() => undefined);
  },
  familyOptions(q: string): Promise<AnnouncementTargetOption[]> {
    return http
      .get<AnnouncementTargetOption[]>('/announcements/family-options', { params: { q } })
      .then((res) => res.data);
  },
  userOptions(q: string): Promise<AnnouncementTargetOption[]> {
    return http
      .get<AnnouncementTargetOption[]>('/announcements/user-options', { params: { q } })
      .then((res) => res.data);
  }
};

// ============================================================
// 积分兑换商品（reward）
// ============================================================

export interface RedemptionItem {
  id: string;
  code: string;
  title: string;
  description: string | null;
  costPoints: number;
  rewardType: string;
  rewardAmount: number;
  rewardRef: string | null;
  icon: string;
  imageUrl: string | null;
  status: string; // 'ON' | 'OFF'
  sortOrder: number;
  createdAt: string;
  updatedAt: string;
}

export interface SaveRedemptionItemBody {
  code?: string; // 仅创建时携带，更新忽略
  title: string;
  description?: string | null;
  costPoints: number;
  rewardType: string;
  rewardAmount: number;
  rewardRef?: string | null;
  icon: string;
  imageUrl?: string | null;
  status: string;
  sortOrder: number;
}

/** 兑换商品配图预览 URL：库里存 C 端 `/api/v1/redemption-images/{file}`，Admin 走同源
 *  `/admin/api/redemption-images/{file}`（已在 AdminSecurityConfig 放行，<img> 不带 token 可取）。 */
export function redemptionImagePreviewUrl(imageUrl?: string | null): string | null {
  if (!imageUrl?.trim()) return null;
  const trimmed = imageUrl.trim();
  if (trimmed.startsWith('http://') || trimmed.startsWith('https://')) {
    return trimmed;
  }
  const match = trimmed.match(/redemption-images\/([^/?#]+)/);
  if (match) {
    const adminBase = import.meta.env.VITE_ADMIN_BASE ?? '';
    return `${adminBase}/admin/api/redemption-images/${match[1]}`;
  }
  return trimmed;
}

export const redemptionItemApi = {
  list(): Promise<RedemptionItem[]> {
    return http.get<RedemptionItem[]>('/redemption-items').then((res) => res.data);
  },
  get(id: string): Promise<RedemptionItem> {
    return http.get<RedemptionItem>(`/redemption-items/${id}`).then((res) => res.data);
  },
  create(body: SaveRedemptionItemBody): Promise<RedemptionItem> {
    return http.post<RedemptionItem>('/redemption-items', body).then((res) => res.data);
  },
  update(id: string, body: SaveRedemptionItemBody): Promise<RedemptionItem> {
    return http.put<RedemptionItem>(`/redemption-items/${id}`, body).then((res) => res.data);
  },
  remove(id: string): Promise<void> {
    return http.delete<void>(`/redemption-items/${id}`).then(() => undefined);
  },
  uploadImage(id: string, file: File): Promise<RedemptionItem> {
    const form = new FormData();
    form.append('file', file);
    return http
      .post<RedemptionItem>(`/redemption-items/${id}/image`, form, {
        headers: { 'Content-Type': 'multipart/form-data' }
      })
      .then((res) => res.data);
  }
};

// ============================================================
// 充值档位（payment）—— 付费购买数字分身额度
// ============================================================

export interface RechargeSku {
  id: string;
  name: string;
  quotaCount: number;
  priceFen: number;
  currency: string;
  appleProductId: string | null;
  sort: number;
  enabled: boolean;
}

export interface SaveRechargeSkuBody {
  name: string;
  quotaCount: number;
  priceFen: number;
  currency?: string;
  appleProductId?: string | null;
  sort: number;
  enabled: boolean;
}

export const rechargeSkuApi = {
  list(): Promise<RechargeSku[]> {
    return http.get<RechargeSku[]>('/recharge-skus').then((res) => res.data);
  },
  get(id: string): Promise<RechargeSku> {
    return http.get<RechargeSku>(`/recharge-skus/${id}`).then((res) => res.data);
  },
  create(body: SaveRechargeSkuBody): Promise<RechargeSku> {
    return http.post<RechargeSku>('/recharge-skus', body).then((res) => res.data);
  },
  update(id: string, body: SaveRechargeSkuBody): Promise<RechargeSku> {
    return http.put<RechargeSku>(`/recharge-skus/${id}`, body).then((res) => res.data);
  },
  remove(id: string): Promise<void> {
    return http.delete<void>(`/recharge-skus/${id}`).then(() => undefined);
  }
};

// ===== 同城活动：发起资格配置（总开关 + 三条门槛） =====

export interface ActivityEligibilityConfig {
  /** 总开关：false=不限制发起（默认）；true=按下列三条 AND 校验。 */
  enabled: boolean;
  /** 门槛①：注册满 N 天。 */
  minRegisterDays: number;
  /** 门槛②：积分余额 ≥ N（仅校验不扣）。 */
  minPoints: number;
  /** 门槛③：是否要求 owner 家庭当前有已激活设备。 */
  requireActiveDevice: boolean;
}

export const activityEligibilityApi = {
  get(): Promise<ActivityEligibilityConfig> {
    return http
      .get<ActivityEligibilityConfig>('/activity/eligibility-config')
      .then((res) => res.data);
  },
  update(body: ActivityEligibilityConfig): Promise<ActivityEligibilityConfig> {
    return http
      .put<ActivityEligibilityConfig>('/activity/eligibility-config', body)
      .then((res) => res.data);
  }
};

// ---------------------------------------------------------------------------
// 在线客服 · 坐席工作台（/admin/api/support/*）
// ---------------------------------------------------------------------------

/** 客服会话（工单）。status: BOT/WAITING/HUMAN/CLOSED。 */
export interface AdminSupportSession {
  userName: string | null;
  userPhone: string | null;
  userUnavailable: boolean;
  id: string;
  /** OPEN(进行中) / CLOSED(已结单)。V303 起 BOT/WAITING/HUMAN 三态退场。 */
  status: string;
  channel: string;
  userId: string | null;
  guest: boolean;
  subject: string | null;
  lastMessagePreview: string | null;
  lastMessageAt: string | null;
  adminUnread: number;
  assigneeAdminId: string | null;
  /** 专属坐席姓名快照；游客公共队列为 null。 */
  assigneeName: string | null;
  createdAt: string | null;
  /** 家庭客服群字段；普通专属客服为空。 */
  familyId: string | null;
  familyName: string | null;
  announcement: string | null;
  familyAvatarUrl: string | null;
}

/** 客服消息。senderRole: USER/BOT(历史AI)/AGENT/SYSTEM；type: TEXT/IMAGE/VOICE。 */
export interface AdminSupportMessage {
  id: string;
  sessionId: string;
  senderRole: string;
  senderId: string | null;
  type: string;
  content: string;
  /** 扩展 JSON 原文；语音为 {"durationMs":12000}。 */
  meta: string | null;
  createdAt: string;
}

/** 坐席人设档案（C 端看到的「那个人」）。 */
export interface AdminSupportAgent {
  id: string;
  adminId: string;
  displayName: string;
  avatarUrl: string | null;
  signature: string | null;
  /** 绑定的飞书成员 Open ID（ou_ 开头）。 */
  feishuOpenId: string | null;
  onlineStatus: string;
  maxSessions: number;
  enabled: boolean;
  sort: number;
  openSessions: number;
}

/** 可加入家庭客服群的后台人员；不要求具备客服坐席档案。 */
export interface AdminSupportStaff {
  adminId: string;
  displayName: string;
}

export interface AdminSupportAgentPayload {
  adminId?: string;
  displayName?: string;
  avatarUrl?: string | null;
  signature?: string | null;
  feishuOpenId?: string | null;
  maxSessions?: number;
  enabled?: boolean;
  sort?: number;
}

/** C 端聊天页底部快捷短语 chip。 */
export interface AdminSupportQuickReply {
  id: string;
  label: string;
  content: string;
  sort: number;
  enabled: boolean;
}

/** 坐席对名下用户的群发批次。 */
export interface AdminSupportBroadcast {
  id: string;
  imageUrl: string | null;
  content: string | null;
  status: string;
  targetCount: number;
  sentCount: number;
  failedCount: number;
  error: string | null;
  createdAt: string | null;
  finishedAt: string | null;
}

export const supportApi = {
  grantPoints(sessionId: string, request: { requestId: string; amount: number; reason: string }): Promise<{ transactionId: string; balanceAfter: number; replayed: boolean }> {
    return http.post(`/support/sessions/${sessionId}/point-grants`, request).then(res => res.data);
  },
  /**
   * 会话队列。
   * @param params.status 逗号分隔（省略=未结单）
   * @param params.scope  mine(默认，自己名下) / unassigned(游客公共队列) / all
   */
  listSessions(
    params: { query?: string; status?: string; scope?: 'mine' | 'unassigned' | 'all'; limit?: number } = {}
  ): Promise<AdminSupportSession[]> {
    return http
      .get<AdminSupportSession[]>('/support/sessions', { params })
      .then((res) => res.data);
  },
  getSession(sessionId: string): Promise<AdminSupportSession> {
    return http
      .get<AdminSupportSession>(`/support/sessions/${sessionId}`)
      .then((res) => res.data);
  },
  messages(sessionId: string, limit = 200): Promise<AdminSupportMessage[]> {
    return http
      .get<AdminSupportMessage[]>(`/support/sessions/${sessionId}/messages`, { params: { limit } })
      .then((res) => res.data);
  },
  /** 坐席回复（AGENT 消息，WAITING→HUMAN）。type 省略为 TEXT。 */
  reply(sessionId: string, content: string, type?: 'TEXT' | 'IMAGE'): Promise<AdminSupportMessage> {
    return http
      .post<AdminSupportMessage>(`/support/sessions/${sessionId}/reply`, { content, type })
      .then((res) => res.data);
  },
  familyAdminMembers(sessionId: string): Promise<string[]> {
    return http
      .get<string[]>(`/support/sessions/${sessionId}/family-admin-members`)
      .then((res) => res.data);
  },
  familyAdminCandidates(): Promise<AdminSupportStaff[]> {
    return http
      .get<AdminSupportStaff[]>('/support/family-admin-candidates')
      .then((res) => res.data);
  },
  inviteFamilyAdmin(sessionId: string, adminId: string): Promise<void> {
    return http
      .post<void>(`/support/sessions/${sessionId}/family-admin-members`, { adminId })
      .then(() => undefined);
  },
  updateFamilyAnnouncement(sessionId: string, announcement: string | null): Promise<void> {
    return http
      .patch<void>(`/support/sessions/${sessionId}/family-announcement`, { announcement })
      .then(() => undefined);
  },
  markRead(sessionId: string): Promise<void> {
    return http.post<void>(`/support/sessions/${sessionId}/read`).then(() => undefined);
  },
  close(sessionId: string): Promise<void> {
    return http.post<void>(`/support/sessions/${sessionId}/close`).then(() => undefined);
  },
  /** 手动改派会话；adminId 省略=解除分配，回到公共队列等待重新分配。 */
  assign(sessionId: string, adminId?: string): Promise<void> {
    return http
      .post<void>(`/support/sessions/${sessionId}/assign`, null, { params: { adminId } })
      .then(() => undefined);
  },
  /**
   * 语音留言 → 可播放的 blob URL。
   *
   * 语音是私密媒体，端点要 Authorization 头，故不能把地址直接丢给 `<audio src>`——
   * 必须走带鉴权的请求取回字节再造 object URL（与反馈语音同一套做法）。
   * 调用方负责在替换/卸载时 `URL.revokeObjectURL`。
   */
  async voiceBlobUrl(voiceKey: string): Promise<string> {
    const filename = voiceKey.split('/').pop() ?? '';
    const res = await http.get<Blob>(`/support/voice/${filename}`, { responseType: 'blob' });
    return URL.createObjectURL(res.data);
  },

  /** 上传客服图片（坐席头像 / 群发配图共用），返回可直接使用的公开地址。 */
  uploadImage(file: File): Promise<string> {
    const form = new FormData();
    form.append('file', file);
    return http
      .post<{ imageUrl: string }>('/support-images', form, {
        headers: { 'Content-Type': 'multipart/form-data' }
      })
      .then((res) => res.data.imageUrl);
  },

  // ---- 坐席档案 ----
  listAgents(): Promise<AdminSupportAgent[]> {
    return http.get<AdminSupportAgent[]>('/support/agents').then((res) => res.data);
  },
  createAgent(payload: AdminSupportAgentPayload): Promise<AdminSupportAgent> {
    return http.post<AdminSupportAgent>('/support/agents', payload).then((res) => res.data);
  },
  updateAgent(id: string, payload: AdminSupportAgentPayload): Promise<AdminSupportAgent> {
    return http.put<AdminSupportAgent>(`/support/agents/${id}`, payload).then((res) => res.data);
  },
  deleteAgent(id: string): Promise<void> {
    return http.delete<void>(`/support/agents/${id}`).then(() => undefined);
  },

  // ---- 快捷短语 ----
  listQuickReplies(): Promise<AdminSupportQuickReply[]> {
    return http.get<AdminSupportQuickReply[]>('/support/quick-replies').then((res) => res.data);
  },
  createQuickReply(payload: Partial<AdminSupportQuickReply>): Promise<AdminSupportQuickReply> {
    return http
      .post<AdminSupportQuickReply>('/support/quick-replies', payload)
      .then((res) => res.data);
  },
  updateQuickReply(
    id: string,
    payload: Partial<AdminSupportQuickReply>
  ): Promise<AdminSupportQuickReply> {
    return http
      .put<AdminSupportQuickReply>(`/support/quick-replies/${id}`, payload)
      .then((res) => res.data);
  },
  deleteQuickReply(id: string): Promise<void> {
    return http.delete<void>(`/support/quick-replies/${id}`).then(() => undefined);
  },

  // ---- 名下群发 ----
  startBroadcast(payload: { imageUrl?: string; content?: string }): Promise<AdminSupportBroadcast> {
    return http.post<AdminSupportBroadcast>('/support/broadcasts', payload).then((res) => res.data);
  },
  getBroadcast(id: string): Promise<AdminSupportBroadcast> {
    return http.get<AdminSupportBroadcast>(`/support/broadcasts/${id}`).then((res) => res.data);
  },
  listBroadcasts(limit = 20): Promise<AdminSupportBroadcast[]> {
    return http
      .get<AdminSupportBroadcast[]>('/support/broadcasts', { params: { limit } })
      .then((res) => res.data);
  }
};

export default http;

export interface ProductModelAppearance {
  id: string;
  nameZh: string;
  nameEn: string;
  imageUrl: string;
  enabled: boolean;
}
export const productModelAppearanceApi = {
  list: (modelId: string) => http.get<ProductModelAppearance[]>(`/product-models/${modelId}/appearances`).then(r => r.data),
  save: (modelId: string, items: ProductModelAppearance[]) => http.put<ProductModelAppearance[]>(`/product-models/${modelId}/appearances`, { items }).then(r => r.data),
  upload: (modelId: string, file: File) => {
    const form = new FormData(); form.append('file', file);
    return http.post<{ url: string }>(`/product-models/${modelId}/appearances/images`, form).then(r => r.data.url);
  },
};
